import { FileValidationError } from '../types';

type ValidateFilesOptions = {
  accept?: string[];
  maxSize?: number;
  maxFiles?: number;
  currentFileCount?: number;
};

/**
 * Validates a list of File objects against the given constraints.
 * Returns an array of validation errors (empty array means all files are valid).
 *
 * Checks are performed in order: maxFiles → maxSize → invalid-type.
 * All violated rules are collected and returned together.
 */
export function validateFiles(
  files: File[],
  options: ValidateFilesOptions
): FileValidationError[] {
  const { accept, maxSize, maxFiles, currentFileCount = 0 } = options;
  const errors: FileValidationError[] = [];

  if (maxFiles != null && currentFileCount + files.length > maxFiles) {
    errors.push({
      code: 'max-files',
      message: `You can upload a maximum of ${maxFiles} file${maxFiles === 1 ? '' : 's'}.`,
    });
  }

  for (const file of files) {
    if (maxSize != null && file.size > maxSize) {
      errors.push({
        code: 'max-size',
        message: `"${file.name}" exceeds the maximum file size of ${formatBytes(maxSize)}.`,
      });
    }

    if (accept != null && accept.length > 0 && !isFileTypeAccepted(file, accept)) {
      errors.push({
        code: 'invalid-type',
        message: `"${file.name}" has an unsupported file type. Accepted: ${accept.join(', ')}.`,
      });
    }
  }

  return errors;
}

function isFileTypeAccepted(file: File, accept: string[]): boolean {
  const fileName = file.name.toLowerCase();
  const mimeType = file.type.toLowerCase();

  return accept.some((pattern) => {
    const normalized = pattern.toLowerCase();

    // Extension match: ".pdf", ".png", etc.
    if (normalized.startsWith('.')) {
      return fileName.endsWith(normalized);
    }

    // Wildcard MIME: "image/*"
    if (normalized.endsWith('/*')) {
      const baseType = normalized.slice(0, -2);
      return mimeType.startsWith(baseType + '/');
    }

    // Exact MIME: "application/pdf"
    return mimeType === normalized;
  });
}

function formatBytes(bytes: number): string {
  if (bytes >= 1_073_741_824) return `${(bytes / 1_073_741_824).toFixed(1)} GB`;
  if (bytes >= 1_048_576) return `${(bytes / 1_048_576).toFixed(1)} MB`;
  if (bytes >= 1_024) return `${(bytes / 1_024).toFixed(1)} KB`;
  return `${bytes} B`;
}
